// Ship-from locations — a logistics partner (3PL), our warehouse, a forwarder.
//
// Saved once on the Billing & settings page, then picked from a list on the
// vendor PO (where the vendor sends the goods) and the commercial invoice
// (where they ship from). Documents freeze the address when issued, so editing
// a location here never changes a document already sent.

import { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, Plus, Star, Trash2 } from 'lucide-react';
import { api, type ListShipFromLocationsResponse, type ShipFromLocation } from '@/lib/api';
import { SHIP_FROM_KINDS, SHIP_FROM_KIND_LABEL, vendorPoShipToText, type ShipFromKind } from '@shared/ship-from';

const QUERY_KEY = ['ship-from-locations'];

/** The tenant's saved locations, for document forms. */
export function useShipFromLocations() {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () =>
      api<ListShipFromLocationsResponse>('/api/billing/ship-from-locations', { tokenGetter: () => getToken() }),
    staleTime: 60_000,
  });
}

const BLANK: Omit<ShipFromLocation, 'id'> = {
  label: '',
  kind: 'logistics_partner',
  companyName: '',
  address: '',
  city: '',
  countryCode: 'US',
  contact: '',
  vendorInstructions: '',
  isDefault: false,
};

const inputCls = 'w-full px-2 py-1.5 border border-border rounded text-sm bg-background';

function Field({ label, required, wide, children }: { label: string; required?: boolean; wide?: boolean; children: React.ReactNode }) {
  return (
    <label className={`block ${wide ? 'sm:col-span-2' : ''}`}>
      <span className="block text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </span>
      {children}
    </label>
  );
}

export function ShipFromLocationsSection() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading } = useShipFromLocations();
  const [drafts, setDrafts] = useState<ShipFromLocation[]>([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data && !dirty) setDrafts(data.locations);
  }, [data, dirty]);

  const save = useMutation({
    mutationFn: () =>
      api<ListShipFromLocationsResponse>('/api/billing/ship-from-locations', {
        method: 'PUT',
        tokenGetter: () => getToken(),
        body: JSON.stringify({ locations: drafts }),
      }),
    onSuccess: (res) => {
      qc.setQueryData(QUERY_KEY, res);
      setDrafts(res.locations);
      setDirty(false);
    },
  });

  const edit = (next: ShipFromLocation[]) => {
    setDrafts(next);
    setDirty(true);
  };
  const update = (idx: number, patch: Partial<ShipFromLocation>) =>
    edit(drafts.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  // New rows get a throwaway id for React keys; the server mints the real one.
  const add = () => edit([...drafts, { ...BLANK, id: `new_${Date.now()}` }]);
  const remove = (idx: number) => edit(drafts.filter((_, i) => i !== idx));
  const toggleDefault = (idx: number) =>
    edit(drafts.map((l, i) => ({ ...l, isDefault: i === idx ? !l.isDefault : false })));

  const invalid = drafts.some(
    (l) => !l.label.trim() || !l.companyName.trim() || !l.address.trim() || !l.city.trim() || l.countryCode.trim().length !== 2,
  );
  const err = save.isError ? (save.error as { message?: string })?.message ?? 'Save failed' : null;

  return (
    <div className="border border-border rounded-lg p-4 space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold">Ship-from locations</h2>
          <p className="text-xs text-muted-foreground mt-0.5 max-w-xl">
            Your logistics partner (3PL) or warehouse. Pick one on a vendor PO to have the vendor ship there,
            and on a commercial invoice to show where the goods ship from. Documents keep the address they were
            issued with.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={add} className="btn-ghost text-xs min-h-9 sm:min-h-0">
            <Plus className="w-3.5 h-3.5" /> Add location
          </button>
          {dirty && (
            <button
              onClick={() => save.mutate()}
              disabled={invalid || save.isPending}
              className="btn-primary text-xs min-h-9 sm:min-h-0 disabled:opacity-50"
            >
              {save.isPending ? 'Saving…' : 'Save locations'}
            </button>
          )}
        </div>
      </div>

      {isLoading && <p className="text-xs text-muted-foreground">Loading…</p>}
      {!isLoading && drafts.length === 0 && (
        <p className="text-xs text-muted-foreground border border-dashed border-border rounded p-4 text-center">
          No locations yet. Add your 3PL so vendor POs and commercial invoices can use it.
        </p>
      )}

      {drafts.map((l, idx) => (
        <div key={l.id} className="border border-border rounded-md bg-surface">
          <div className="px-3 py-2 border-b border-border flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" aria-hidden />
              <span className="font-medium text-sm truncate">{l.label || 'New location'}</span>
              {l.isDefault && (
                <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[11px] font-medium">Default</span>
              )}
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                onClick={() => toggleDefault(idx)}
                className="text-xs px-2 py-1.5 sm:py-0.5 border border-border rounded hover:bg-muted inline-flex items-center gap-1"
                title="Preselect this location on new documents"
              >
                <Star className="w-3 h-3" /> {l.isDefault ? 'Unset default' : 'Make default'}
              </button>
              <button
                onClick={() => remove(idx)}
                className="text-xs px-2 py-1.5 sm:py-0.5 border border-border rounded text-destructive hover:bg-destructive/10 inline-flex items-center gap-1"
              >
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            </div>
          </div>
          <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Name in lists (e.g. “Miami 3PL”)" required>
              <input className={inputCls} value={l.label} onChange={(e) => update(idx, { label: e.target.value })} />
            </Field>
            <Field label="Type">
              <select className={inputCls} value={l.kind} onChange={(e) => update(idx, { kind: e.target.value as ShipFromKind })}>
                {SHIP_FROM_KINDS.map((k) => <option key={k} value={k}>{SHIP_FROM_KIND_LABEL[k]}</option>)}
              </select>
            </Field>
            <Field label="Company name on documents" required wide>
              <input className={inputCls} value={l.companyName} onChange={(e) => update(idx, { companyName: e.target.value })} />
            </Field>
            <Field label="Address (one line per row)" required wide>
              <textarea className={inputCls} rows={3} value={l.address} onChange={(e) => update(idx, { address: e.target.value })} />
            </Field>
            <Field label="City (the FCA origin)" required>
              <input className={inputCls} value={l.city} onChange={(e) => update(idx, { city: e.target.value })} />
            </Field>
            <Field label="Country (2 letters)" required>
              <input
                className={inputCls}
                maxLength={2}
                value={l.countryCode}
                onChange={(e) => update(idx, { countryCode: e.target.value.toUpperCase() })}
              />
            </Field>
            <Field label="Receiving contact / phone" wide>
              <input className={inputCls} value={l.contact} onChange={(e) => update(idx, { contact: e.target.value })} />
            </Field>
            <Field label="Extra instructions for vendors shipping here (optional)" wide>
              <textarea
                className={inputCls}
                rows={2}
                placeholder="e.g. Attn: APD receiving · Suite 4 · Dock open 8am–4pm"
                value={l.vendorInstructions}
                onChange={(e) => update(idx, { vendorInstructions: e.target.value })}
              />
            </Field>
          </div>
        </div>
      ))}

      {invalid && dirty && (
        <p className="text-[11px] text-destructive">Fill in the starred fields on every location before saving.</p>
      )}
      {err && <p className="text-[11px] text-destructive">{err}</p>}
    </div>
  );
}

/**
 * Pick a saved location for a document. Calls onPick with the location (or
 * null for "none"); the caller decides what to fill in. Renders a link to
 * settings when no locations exist yet, so the option is discoverable.
 */
export function ShipFromPicker({
  value,
  onPick,
  noneLabel = 'None',
  className = inputCls,
}: {
  value: string;
  onPick: (location: ShipFromLocation | null) => void;
  noneLabel?: string;
  className?: string;
}) {
  const { data, isLoading } = useShipFromLocations();
  const locations = data?.locations ?? [];
  if (!isLoading && locations.length === 0) {
    return (
      <p className="text-[11px] text-muted-foreground">
        No saved locations. Add your 3PL under <a href="/billing" className="underline">Billing &amp; settings</a>.
      </p>
    );
  }
  return (
    <select
      className={className}
      value={value}
      disabled={isLoading}
      onChange={(e) => onPick(locations.find((l) => l.id === e.target.value) ?? null)}
    >
      <option value="">{noneLabel}</option>
      {locations.map((l) => (
        <option key={l.id} value={l.id}>
          {l.label} · {l.city}
        </option>
      ))}
    </select>
  );
}

export { vendorPoShipToText };
